import React from 'react'
import { Modal } from 'react-bootstrap'
import { BusyButton } from '@ztechapps/webui.button'
import { HorizontalStack, Icon, ShowIf } from '@ztechapps/webui.util'
import styles from './ConfirmModal.module.scss'
import { DraggableModal } from '../Modal/DraggableModal'
import { ModalDragHandle } from '../DragHandle'

export type ConfirmModalIcon = 'warning' | 'error' | 'success' | 'info'

export type ConfirmModalButtons = {
  cancel?: string | false
  no?: { text?: string }
  confirm?: { text?: string }
}

export type ConfirmModalProps = {
  show: boolean
  title?: string
  text?: string
  icon?: ConfirmModalIcon
  dangerMode?: boolean
  buttons?: ConfirmModalButtons
  children?: React.ReactNode
  onConfirm: () => void
  onNo?: () => void
  onCancel: () => void
  size?: 'sm' | 'lg' | 'xl'
}

const iconNameMap: Record<ConfirmModalIcon, string> = {
  warning: 'exclamation-triangle',
  error: 'times-circle',
  success: 'check-circle',
  info: 'info-circle',
}

const iconStyleMap: Record<ConfirmModalIcon, string> = {
  warning: styles.iconWarning,
  error: styles.iconError,
  success: styles.iconSuccess,
  info: styles.iconInfo,
}

export const ConfirmModal = ({
  show,
  title,
  text,
  icon,
  dangerMode = false,
  buttons,
  children,
  onConfirm,
  onNo,
  onCancel,
  size = null,
}: ConfirmModalProps) => {
  const cancelText = buttons?.cancel === false ? false : (buttons?.cancel ?? 'Cancel')
  const confirmText = buttons?.confirm?.text ?? 'OK'
  const showCancelButton = cancelText !== false
  // Optional middle button (e.g. "NO") — rendered only when the dialog config declares it
  const noText = buttons?.no?.text
  const showNoButton = !!noText && !!onNo

  return (
    <Modal
      dialogAs={DraggableModal}
      show={show}
      onHide={onCancel}
      backdrop="static"
      centered
      size={size}
      animation={false}
      className={styles.confirmModalRaised}
      backdropClassName={styles.confirmBackdropRaised}
    >
      <ModalDragHandle></ModalDragHandle>

      <Modal.Body>
        <ShowIf condition={!!icon}>
          <div className={`${styles.iconContainer} ${iconStyleMap[icon as ConfirmModalIcon] ?? ''}`}>
            <Icon name={iconNameMap[icon as ConfirmModalIcon]} />
          </div>
        </ShowIf>
        <ShowIf condition={!!title}>
          <div className={styles.title}>{title}</div>
        </ShowIf>
        <ShowIf condition={!!text}>
          <div className={styles.textContent}>{text}</div>
        </ShowIf>
        <div>{children}</div>
        <HorizontalStack gap={2} className="mt-5 justify-content-end">
          <ShowIf condition={showCancelButton}>
            {/* Was `variant="secondary"` (a SOLID fill). Any app
                that overrides `$secondary` toward the danger family — QuickScore's brand rose,
                `#c77d8a` — renders this safe escape as a second filled red-family button of equal
                visual weight right beside `dangerMode`'s filled `btn-danger`, so colour no longer
                distinguishes safe from destructive (text contrast passes on both; the failure is
                semantic weight, not contrast). `outline-secondary` — already used one line below
                for the optional "no" button — keeps Cancel identifiable via `$secondary` without
                filling it solid, matching the prototype's plain, lower-weight Cancel button
                (docs/support/spec/2026-09-10-feature-1476-support-ticket-batch-operations.prototype.html:187). */}
            <BusyButton variant="outline-secondary" onClick={onCancel} text={cancelText as string} />
          </ShowIf>
          <ShowIf condition={showNoButton}>
            <BusyButton variant="outline-secondary" onClick={onNo} text={noText as string} />
          </ShowIf>
          <BusyButton variant={dangerMode ? 'danger' : 'primary'} onClick={onConfirm} text={confirmText} />
        </HorizontalStack>
      </Modal.Body>
    </Modal>
  )
}
